import {useTranslation} from 'react-i18next';

import {ScoreStatCard} from '../../../../../components/ScoreStatCard/ScoreStatCard';
import {getBenchmarkDeltaVariant} from '../../../../../components/ScoreStatCard/scoreStatUtils';
import {StatsGridContainer} from '../../../../../components/StatsGridContainer/StatsGridContainer';
import {
  DEPARTMENT_HEALTH_DIMENSIONS,
  type DepartmentHealthBenchmarks,
  type DepartmentHealthScores,
} from '../../types/DepartmentHealthTypes';
import {getDepartmentHealthBenchmarkReference} from '../../utils/DepartmentHealth/getDepartmentHealthBenchmarkReference';

const scoreGridSx = {
  gap: 1.5,
  mb: 2.5,
  gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)'},
} as const;

export interface Props {
  scores?: DepartmentHealthScores;
  benchmarks?: DepartmentHealthBenchmarks;
  loading?: boolean;
}

export const DepartmentHealthScoreGrid = ({scores, benchmarks, loading = false}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDepartmentHealthPage.scoreCards'});

  if (loading || !scores || !benchmarks) {
    return <StatsGridContainer loading skeletonCount={4} skeletonHeight={110} sx={scoreGridSx} />;
  }

  const departmentAverage = scores.departmentAverage;

  return (
    <StatsGridContainer sx={scoreGridSx}>
      {DEPARTMENT_HEALTH_DIMENSIONS.map((dimension) => {
        const score = departmentAverage?.[dimension] ?? null;
        const reference = getDepartmentHealthBenchmarkReference(benchmarks, dimension);
        const delta = benchmarks.deltas?.[dimension] ?? null;
        const deltaVariant = getBenchmarkDeltaVariant(delta);

        const deltaLabel =
          deltaVariant === 'flat'
            ? t('delta.approxNorm')
            : deltaVariant === 'up'
              ? t('delta.vsNorm', {delta: `+${delta}`})
              : deltaVariant === 'down'
                ? t('delta.vsNorm', {delta})
                : null;

        const normLabel = reference !== null ? t('normLabel', {value: reference}) : null;

        return (
          <ScoreStatCard
            key={dimension}
            label={t(`dimensions.${dimension}`)}
            score={score}
            deltaLabel={deltaLabel}
            normLabel={normLabel}
            deltaVariant={deltaVariant}
          />
        );
      })}
    </StatsGridContainer>
  );
};
